import * as React from 'react';
import { GridBody } from './GridBody';
import { ColumnDefinition, TABLE_SORT_DIRECTION } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE } from 'Src/consts/table';
import './Grid.scss';
import { GridTitle2 } from './GridTitle2';
import { SelectOption } from '../../helpers/table/table';
import { GridPanel } from './GridPanel';
import { ActionItem } from '../ActionsButton/ActionsButton';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from '../../models/tournament';
import { Pagination2 } from '../Pagination/Pagination2';
import {
  getColumnVisibilityStorageKey,
  loadHiddenColumns,
  saveHiddenColumns
} from 'Src/helpers/columnVisibility/columnVisibility';
import { isUserAdminSU } from 'Src/helpers/user/user';

interface Props {
  dataItems: any[];
  onItemClick: (index: number) => void;
  onItemSelect?: (index: number) => void;
  onSortClick: (sortField: string) => void;
  columns: ColumnDefinition[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  dataItemsSelected: any[];
  onApplyFilterClick: () => void;
  onClearFilterClick: () => void;
  onClearIndividualFilter?: (field: string) => void;
  actionItems: ActionItem[];
  currentPage: number;
  total: number;
  pageSize?: number;
  isSelectAllChecked: boolean;
  selectedItemsCount?: number;
  isDataFiltered: boolean;
  setCurrentPageParams: (currentPage: number) => void;
  onSelectAllClick: () => void;
  onSelectAllOnPageClick: () => void;
  onUnselectAllClick: () => void;
  onTableFilterClick: (event) => void;
  onTableFilterChange?: (event, value: string, options?, index?: number, checkBoxIndex?: number) => void;
  onCancelFilterClick?: () => void;
  onCellIconClick?: (data, cell) => void;
  renderAdditionalOptions?: () => React.ReactNode;
  filters: any;
  goBack?: () => void;
  goBackLabel?: string;
  gridTitle?: string;
  gridTitleContent?: React.ReactNode;
  gridEyebrow?: string;
  gridSubtitle?: string;
  gridTitleCount?: number;
  emptyText?: string;
  emptyHint?: string;
  emptyFilteredText?: string;
  emptyFilteredHint?: string;
  emptyIcon?: any;
  toolbarSearchField?: string;
  toolbarSearchPlaceholder?: string;
  onToolbarSearchClear?: () => void;
  isSearchHidden?: boolean;
  isActionsInline?: boolean;
  selectionActionsBelow?: boolean;
  showClearSelection?: boolean;
  hiddenFilterFields?: string[];
  isPDF?: boolean;
  isCSV?: boolean;
  isSelectDisabled?: boolean;
  isSelectionHidden?: boolean;
  isDataItemSelectionDisabled?: (item: any) => boolean;
  stylizeCell?: (cell: any) => string;
  autocompleteSearchFunctions?: any;
  autocompleteGetElementsTitles?: any;
  onTableAutocompleteFilterChange?: (item: any, field: string) => any;
  autocompleteDefaultTexts?: any;

  user?: AppUser;
  tournament?: Tournament;

  options?: SelectOptions;
  filterMinDateInterval?: Date;
  filterMaxDateInterval?: Date;
  columnVisibilityKey?: string;
  fixedColumnFields?: string[];
  defaultHiddenColumns?: string[];
  filterOnlyFields?: string[];
  hiddenColumnToggleFields?: string[];
}

export interface SelectOptions {
  tournamentAvailability?: SelectOption[];
  availability?: SelectOption[];
  schoolId?: SelectOption[];
  priority?: SelectOption[];
  invitationStatus?: SelectOption[];
  generalMessageStatus?: SelectOption[];
  generalMessageDeliveryStatus?: SelectOption[];
  clubMessageStatus?: SelectOption[];
  participant?: SelectOption[];
  ages?: SelectOption[];
  agesRecord?: SelectOption[];
  days?: SelectOption[];
  userStatus?: SelectOption[];
  forms?: SelectOption[];
  houses?: SelectOption[];
  houseId?: SelectOption[];
  genders?: SelectOption[];
  tag?: SelectOption[];
  role?: SelectOption[];
  clubStatus?: SelectOption[];
  permissionFormIds?: SelectOption[];
  permissionHouseIds?: SelectOption[];
  ageGroup?: SelectOption[];
  deliveryStatus?: SelectOption[];
  statusOptions?: SelectOption[];
  requestedPermissionPreset?: SelectOption[];
  messageType?: SelectOption[];
  status?: SelectOption[];
  season?: SelectOption[];
  sportId?: SelectOption[];
  eventTournamentGroup?: SelectOption[];
  action?: SelectOption[];
  tournamentAges?: SelectOption[];
  participationStatisticAges?: SelectOption[];
  pupilsPremium?: SelectOption[];
  specialEducationNeeds?: SelectOption[];
  freeSchoolMeals?: SelectOption[];
  gender?: SelectOption[];
  inviteStatus?: SelectOption[];
  userFormName?: SelectOption[];
  userHouseName?: SelectOption[];
  eventType?: SelectOption[];
  eventAges?: SelectOption[];
  venueType?: SelectOption[];
  eventSport?: SelectOption[];
  aggregationType?: SelectOption[];
  subtype?: SelectOption[];
  isPublishOnTheWebsite?: SelectOption[];
  isPromoteTournament?: SelectOption[];
  isPublished?: SelectOption[];
  category?: SelectOption[];
  upcomingTournamentsSeason?: SelectOption[];
  pastTournamentsSeason?: SelectOption[];
  tournamentTag?: SelectOption[];
  availableAges?: SelectOption[];
  availableGender?: SelectOption[];
  areaSU?: SelectOption[];
}

export const Grid2: React.FunctionComponent<Props> = props => {
  const isModernGrid = Boolean(props.isActionsInline || (props.user && isUserAdminSU(props.user)));
  const routeKey = typeof window !== 'undefined' ? window.location.pathname : 'grid';
  const columnVisibilityKey = props.columnVisibilityKey || `${routeKey}:list`;
  const fixedColumnFields = props.fixedColumnFields || [];
  const filterOnlyFields = props.filterOnlyFields || [];
  const hiddenColumnToggleFields = props.hiddenColumnToggleFields || [];
  const [hiddenColumns, setHiddenColumns] = React.useState<string[]>(() => {
    let hasStoredPreference = false;
    try {
      hasStoredPreference =
        typeof window !== 'undefined' &&
        window.localStorage.getItem(getColumnVisibilityStorageKey(props.user, columnVisibilityKey)) !== null;
    } catch (error) {
      hasStoredPreference = false;
    }
    const initialColumns = hasStoredPreference
      ? loadHiddenColumns(props.user, columnVisibilityKey)
      : props.defaultHiddenColumns || [];
    return initialColumns.filter(field => !fixedColumnFields.includes(field));
  });
  const updateHiddenColumns = (updater: (current: string[]) => string[]) => {
    setHiddenColumns(current => {
      const next = updater(current);
      saveHiddenColumns(props.user, columnVisibilityKey, next);
      return next;
    });
  };
  const effectiveHiddenColumns = Array.from(new Set([...hiddenColumns, ...filterOnlyFields]));
  const visibleColumns =
    isModernGrid && effectiveHiddenColumns.length > 0
      ? props.columns.filter(column => !effectiveHiddenColumns.includes(column.field))
      : props.columns;
  const toolbarSearchField = props.isSearchHidden
    ? undefined
    : props.toolbarSearchField ||
      (isModernGrid
        ? props.columns.find(
            column =>
              column.type === FILTER_TYPE.TEXT &&
              !filterOnlyFields.includes(column.field) &&
              !props.hiddenFilterFields?.includes(column.field)
          )?.field
        : undefined);
  const clearIndividualFilter = (field: string) => {
    if (props.onClearIndividualFilter) {
      props.onClearIndividualFilter(field);
      return;
    }
    if (!props.onTableFilterChange) return;

    const column = props.columns.find(candidate => candidate.field === field);
    if (
      column?.type === FILTER_TYPE.DATE_INTERVAL ||
      column?.type === FILTER_TYPE.DATETIME_INTERVAL ||
      column?.type === FILTER_TYPE.TIME_INTERVAL
    ) {
      props.onTableFilterChange({ target: { value: '' } }, field, DATE_INTERVAL.FROM);
      window.setTimeout(() => {
        props.onTableFilterChange!({ target: { value: '' } }, field, DATE_INTERVAL.TO);
        window.setTimeout(props.onApplyFilterClick, 0);
      }, 0);
      return;
    }
    if (column?.type === FILTER_TYPE.MULTISELECT) {
      const options = (((props.options as any) && (props.options as any)[field]) || []).map(option => ({
        value: String(option.value),
        selected: false
      }));
      props.onTableFilterChange({ target: { value: [], options } }, field);
    } else {
      props.onTableFilterChange({ target: { value: '' } }, field);
    }
    window.setTimeout(props.onApplyFilterClick, 0);
  };
  const clearToolbarSearch =
    props.onToolbarSearchClear ||
    (toolbarSearchField && props.onTableFilterChange ? () => clearIndividualFilter(toolbarSearchField) : undefined);

  return (
    <div className={isModernGrid ? 'gridModernRoot' : undefined}>
      <GridPanel
        actionItems={props.actionItems}
        currentPage={props.currentPage}
        setCurrentPageParams={props.setCurrentPageParams}
        total={props.total}
        onSelectAllClick={props.onSelectAllClick}
        onSelectAllOnPageClick={props.onSelectAllOnPageClick}
        onUnselectAllClick={props.onUnselectAllClick}
        isSelectAllChecked={props.isSelectAllChecked}
        selectedItems={props.dataItemsSelected}
        selectedItemsCount={props.selectedItemsCount}
        onTableFilterClick={props.onTableFilterClick}
        isShowFilter={props.isShowFilter}
        isDataFiltered={props.isDataFiltered}
        goBack={props.goBack}
        goBackLabel={props.goBackLabel}
        gridTitle={props.gridTitle}
        gridTitleContent={props.gridTitleContent}
        gridEyebrow={props.gridEyebrow}
        gridSubtitle={props.gridSubtitle}
        gridTitleCount={props.gridTitleCount}
        toolbarSearchField={toolbarSearchField}
        toolbarSearchPlaceholder={props.toolbarSearchPlaceholder}
        onToolbarSearchClear={clearToolbarSearch}
        isActionsInline={isModernGrid}
        selectionActionsBelow={props.selectionActionsBelow}
        showClearSelection={props.showClearSelection}
        hiddenFilterFields={props.hiddenFilterFields}
        isPDF={props.isPDF}
        isCSV={props.isCSV}
        columns={props.columns}
        allColumns={props.columns.filter(
          column => !filterOnlyFields.includes(column.field) && !hiddenColumnToggleFields.includes(column.field)
        )}
        hiddenColumns={effectiveHiddenColumns}
        fixedColumnFields={fixedColumnFields}
        onToggleColumn={field => {
          if (fixedColumnFields.includes(field)) return;
          updateHiddenColumns(current =>
            current.includes(field) ? current.filter(columnField => columnField !== field) : [...current, field]
          );
        }}
        onResetColumns={() =>
          updateHiddenColumns(() =>
            (props.defaultHiddenColumns || []).filter(field => !fixedColumnFields.includes(field))
          )
        }
        filters={props.filters}
        options={props.options as any}
        onClearFilterClick={props.onClearFilterClick}
        onClearIndividualFilter={clearIndividualFilter}
        onApplyFilterClick={props.onApplyFilterClick}
        onCancelFilterClick={props.onCancelFilterClick}
        onTableFilterChange={props.onTableFilterChange}
        isSelectDisabled={props.isSelectDisabled}
        renderAdditionalOptions={props.renderAdditionalOptions}
      />
      <div className="table-responsive">
        <table className="table table-sm table-hover mt-3 mPre mVerticalAlignMiddle mVerticalAlignTop">
          <GridTitle2
            sortColumnsName={props.sortColumnsName}
            sortDirection={props.sortDirection}
            columns={visibleColumns}
            onSortClick={props.onSortClick}
            isShowFilter={props.isShowFilter}
            onApplyFilterClick={props.onApplyFilterClick}
            onClearFilterClick={props.onClearFilterClick}
            filters={props.filters}
            options={props.options}
            onTableFilterChange={props.onTableFilterChange}
            filterMinDateInterval={props.filterMinDateInterval}
            filterMaxDateInterval={props.filterMaxDateInterval}
            autocompleteSearchFunctions={props.autocompleteSearchFunctions}
            autocompleteGetElementsTitles={props.autocompleteGetElementsTitles}
            onTableAutocompleteFilterChange={props.onTableAutocompleteFilterChange}
            autocompleteDefaultTexts={props.autocompleteDefaultTexts}
            isModernList={isModernGrid}
            isSelectAllChecked={props.isSelectAllChecked}
            onSelectAllOnPageClick={props.onSelectAllOnPageClick}
            onSelectAllClick={props.onSelectAllClick}
            onUnselectAllClick={props.onUnselectAllClick}
            total={props.total}
            currentPage={props.currentPage}
            selectedItems={props.dataItemsSelected}
            pageItemsCount={props.dataItems.length}
            selectedItemsCount={props.selectedItemsCount}
            isSelectDisabled={props.isSelectDisabled}
            isSelectionHidden={props.isSelectionHidden}
          />
          <GridBody
            isDataItemSelectionDisabled={props.isDataItemSelectionDisabled}
            dataItems={props.dataItems}
            columns={visibleColumns}
            dataItemsSelected={props.dataItemsSelected}
            onDataItemClick={props.onItemClick}
            onDataItemSelect={props.onItemSelect}
            isSelectDisabled={props.isSelectDisabled}
            isSelectionHidden={props.isSelectionHidden}
            user={props.user}
            tournament={props.tournament}
            stylizeCell={props.stylizeCell}
            onCellIconClick={props.onCellIconClick}
            emptyText={props.emptyText}
            emptyHint={props.emptyHint}
            emptyFilteredText={props.emptyFilteredText}
            emptyFilteredHint={props.emptyFilteredHint}
            emptyIcon={props.emptyIcon}
            isDataFiltered={props.isDataFiltered}
          />
        </table>
      </div>
      {isModernGrid && props.total > 0 && (
        <div className="gridModernPagination">
          <Pagination2
            currentPage={props.currentPage}
            setCurrentPageParams={props.setCurrentPageParams}
            total={props.total}
            modern={true}
            limit={props.pageSize}
          />
        </div>
      )}
    </div>
  );
};

Grid2.defaultProps = {
  isPDF: false,
  isCSV: true,
  isSelectDisabled: false
};

Grid2.displayName = 'Grid2';
